import React from "react";
import type { OnChangeValue } from "react-select";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { compact } from "lodash";

import EmailListInput, { type Contact } from "~/common/EmailListInput";
import UserMultiSelector, {
	type MultiValueType,
	type UserMultiSelectorOption,
} from "~/common/UserSelect/UserMultiSelector";
import Popup from "~/ui/legacy/Popup";
import Switch from "~/ui/Switch/Switch";

import type { UserType } from "./types";

import stylessettings from "~/settings/css/settings.module.css";
import stylesShareModal from "~/stream/css/ShareModal.module.css";
import stylesMeetingReminders from "~/stream/StreamSettings/Automations/MeetingReminders.module.css";
import stylesTimeSaver from "~/stream/StreamSettings/Automations/TimeSaver.module.css";
import stylesStreamSettings from "~/stream/StreamSettings/StreamSettings.module.css";

function AllParticipantsTooltip() {
	return (
		<Popup
			inverted
			size="tiny"
			content="All participants includes meeting attendees who are not in Fellow."
			position="top center"
			trigger={<FontAwesomeIcon icon={faInfoCircle} className={stylesTimeSaver.infoIcon} />}
			className={stylesTimeSaver.tooltip}
		/>
	);
}

interface UserEmailInputsProps {
	allParticipantsValue: boolean;
	onAllParticipantsChange: (checked: boolean) => void;
	userIdsValue: string[];
	onUserIdsChange: (v: string[]) => void;
	usersWithAccess: UserType[];
}

export default function UserEmailInputs({
	allParticipantsValue,
	onAllParticipantsChange,
	userIdsValue,
	onUserIdsChange,
	usersWithAccess,
}: UserEmailInputsProps) {
	const users: { [k: string]: UserType } = {};

	for (const user of usersWithAccess) {
		users[user.id] = user;
	}

	const allowedUserIds = Object.keys(users);

	const selectedUsers: UserMultiSelectorOption[] = compact(
		userIdsValue.map(id => {
			if (!users[id]) {
				// It might be possible if the user was removed from the stream after the automation was set before
				return undefined;
			}

			const userOption = {
				value: users[id].id,
				label: users[id].fullName,
				avatar: {
					id: users[id].id,
					fullName: users[id].fullName,
					isGuest: users[id].isGuest || false,
					image: users[id].image || "",
					email: users[id].email || "",
				},
			};

			return userOption;
		}),
	);

	const onChange = React.useCallback(
		(newUsers: MultiValueType<UserMultiSelectorOption>) => {
			const userIds = newUsers.map(user => user.value);
			onUserIdsChange(userIds);
		},
		[onUserIdsChange],
	);

	return (
		<>
			<div className={cx(stylesMeetingReminders.row, stylesTimeSaver.row)}>
				<label className={stylesTimeSaver.label}>
					Remind all participants:
					<AllParticipantsTooltip />
				</label>
				<Switch
					checked={allParticipantsValue}
					onCheckedChange={onAllParticipantsChange}
					className={cx(stylesStreamSettings.toggle, stylessettings.settingsPageToggle)}
				/>
			</div>

			{allParticipantsValue === false && (
				<>
					<label
						className={cx(
							stylesMeetingReminders.row,
							stylesTimeSaver.row,
							stylesMeetingReminders.tight,
							stylesTimeSaver.label,
						)}
					>
						Remind specific participants:
					</label>
					<div className={cx(stylesMeetingReminders.row, stylesTimeSaver.row)}>
						<UserMultiSelector
							ranked
							textOnly
							placeholder="Select one or more participants by name or email…"
							idIn={allowedUserIds}
							selectedUsers={selectedUsers}
							onChange={onChange}
							className={stylesShareModal.userSelect}
						/>
					</div>
				</>
			)}
		</>
	);
}

interface EmailInputsProps {
	allParticipantsValue: boolean;
	onAllParticipantsChange: (checked: boolean) => void;
	emailsValue: string[];
	onEmailsChange: (v: string[]) => void;
}

export function EmailInputs({
	allParticipantsValue,
	onAllParticipantsChange,
	emailsValue,
	onEmailsChange,
}: EmailInputsProps) {
	const onChange = React.useCallback(
		(options: OnChangeValue<Contact, true>) => {
			let emails: string[] = [];

			if (options && "length" in options) {
				emails = options.map(contact => contact.email);
			}

			onEmailsChange(emails);
		},
		[onEmailsChange],
	);

	const selectedOptions = React.useMemo(() => emailsValue.map(email => ({ id: email, email })), [emailsValue]);

	return (
		<>
			<div className={cx(stylesMeetingReminders.row, stylesTimeSaver.row)}>
				<label className={stylesTimeSaver.label}>
					Send notes to all participants:
					<AllParticipantsTooltip />
				</label>
				<Switch
					checked={allParticipantsValue}
					onCheckedChange={onAllParticipantsChange}
					className={cx(stylesStreamSettings.toggle, stylessettings.settingsPageToggle)}
				/>
			</div>

			{allParticipantsValue === false && (
				<>
					<label
						className={cx(
							stylesMeetingReminders.row,
							stylesTimeSaver.row,
							stylesMeetingReminders.tight,
							stylesTimeSaver.label,
						)}
					>
						Send notes to specific people:
					</label>
					<div className={cx(stylesMeetingReminders.row, stylesTimeSaver.row)}>
						<EmailListInput
							placeholder="Add one or more people by name or email…"
							onChange={onChange}
							value={selectedOptions}
							isMulti
						/>
					</div>
				</>
			)}
		</>
	);
}
